import { useContext, useMemo } from 'react';
import { Spinner } from '@fluentui/react-components';
import { CheckmarkFilled, EditOffRegular, EditRegular, EyeFilled, LockClosedRegular } from '@fluentui/react-icons';
import type { TOverflowButtonParam } from '@shared/components/Overflow';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { GENERIC_ERROR_MESSAGE } from '@shared/i18n/messages';
import { AUTO_EDIT_QUERY_PARAM, AUTO_EDIT_QUERY_VALUE } from '@modules/items/constants';
import DocumentContext from '../contexts/DocumentContext.context';
import { useShowPartLoading } from '../largeDocument';
import useDelayedIndicator from './useDelayedIndicator.hook';
import useEmbeddedEditMode from './useEmbeddedEditMode.hook';

type TOptions = {
  hasDraft: boolean;
  // Why editing is locked, empty when it is not.
  disabledReason: string;
  // Given while an older version is shown.
  viewLatest?: () => void;
  // For a page that creates its draft itself; otherwise editing creates it.
  createDraft?: { onClick: () => void; isPending: boolean };
  // The page's edit mode outlives the shown document, in `autoEdit`.
  keepsEditIntent?: boolean;
  // The page's edit mode waits for a document that has not loaded yet.
  isEditorPending?: boolean;
  onComplete?: () => void;
};

const useEditNavActions = ({
  hasDraft,
  disabledReason,
  viewLatest,
  createDraft,
  keepsEditIntent,
  isEditorPending,
  onComplete,
}: TOptions): { editAction: TOverflowButtonParam; completeAction?: TOverflowButtonParam } => {
  const { change } = useSearchParams();
  // Missing while no document is shown.
  const documentContext = useContext(DocumentContext);
  const { autoEdit } = useEmbeddedEditMode();
  const showPartLoading = useShowPartLoading();
  const connecting = Boolean(documentContext?.requestEdit && !documentContext.isEdit);
  // A page that keeps edit mode connects each next document by itself, usually at once, and already says Stop editing.
  const isSlowToConnect = useDelayedIndicator(connecting, { onlyWhenSlow: true });
  const hasUnsupportedContent = Boolean(documentContext?.hasUnsupportedContent);
  const editLabel = hasDraft ? 'Edit draft' : 'Create draft';

  const getEditAction = (): TOverflowButtonParam => {
    if (viewLatest)
      return {
        children: `View ${hasDraft ? 'draft' : 'latest'}`,
        icon: <EyeFilled />,
        onClick: viewLatest,
      };
    if (disabledReason)
      return {
        children: editLabel,
        icon: <LockClosedRegular />,
        disabled: true,
        disabledMessage: disabledReason,
      };
    if (!hasDraft && createDraft)
      return {
        appearance: 'primary',
        children: 'Create draft',
        icon: createDraft.isPending ? <Spinner size="tiny" /> : <EditRegular />,
        onClick: createDraft.onClick,
        disabled: createDraft.isPending,
      };
    if (documentContext?.isEdit)
      return {
        appearance: 'primary',
        // A part can take a second on a large document, and a click with no feedback reads as
        // dead. The label changes too: a spinner on "Stop editing" says the wrong thing.
        children: showPartLoading ? 'Loading' : 'Stop editing',
        icon: showPartLoading ? <Spinner size="tiny" /> : <EditOffRegular />,
        // The session drops the edit intent once its save succeeded; a failed save keeps the document editing.
        onClick: documentContext.viewMode,
      };
    // Drawn as before until it is slow, but inert: stopping mid-connect would leave the document editing.
    if (connecting && keepsEditIntent && !isSlowToConnect)
      return { appearance: 'primary', children: 'Stop editing', icon: <EditOffRegular /> };
    if (connecting)
      return {
        appearance: 'primary',
        children: keepsEditIntent ? 'Stop editing' : editLabel,
        icon: <Spinner size="tiny" />,
        disabled: true,
      };
    // Edit mode is on, but the shown document is not editing, e.g. because someone else holds it.
    if (keepsEditIntent && autoEdit)
      return {
        appearance: 'primary',
        children: 'Stop editing',
        icon: <EditOffRegular />,
        onClick: () => change(AUTO_EDIT_QUERY_PARAM, undefined),
      };
    return {
      appearance: 'primary',
      children: editLabel,
      icon: <EditRegular />,
      onClick: documentContext ? documentContext.editDraft : () => change(AUTO_EDIT_QUERY_PARAM, AUTO_EDIT_QUERY_VALUE),
    };
  };

  return useMemo(
    () => ({
      editAction: getEditAction(),
      completeAction: onComplete && {
        children: 'Complete version',
        icon: <CheckmarkFilled />,
        onClick: onComplete,
        disabled:
          Boolean(disabledReason) ||
          connecting ||
          Boolean(isEditorPending) ||
          Boolean(documentContext?.isAutoEditPending) ||
          hasUnsupportedContent,
        disabledMessage: hasUnsupportedContent ? GENERIC_ERROR_MESSAGE : disabledReason || 'Reconnecting...',
      },
    }),
    // Everything the buttons read, getEditAction included.
    [
      hasDraft,
      disabledReason,
      viewLatest,
      createDraft?.onClick,
      createDraft?.isPending,
      keepsEditIntent,
      isEditorPending,
      onComplete,
      documentContext?.isEdit,
      documentContext?.viewMode,
      documentContext?.editDraft,
      connecting,
      isSlowToConnect,
      documentContext?.isAutoEditPending,
      hasUnsupportedContent,
      autoEdit,
      showPartLoading,
    ]
  );
};

export default useEditNavActions;
